import React, { useState, useRef, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { HiArchiveBox, HiChevronDown, HiPlus } from 'react-icons/hi2';
import { useNavigate, useLocation } from 'react-router-dom';
import { VBButton } from '../ui/VBButton';
import { OrganizationModal } from '../modals/OrganizationModal';
import { DeactivatedOrgsModal } from '../modals/DeactivatedOrgsModal';
import { ProfileDropdown } from '../ui/ProfileDropdown';
import { useOrg } from '../../providers/OrgProvider';
import { usePermissions } from '../../hooks/usePermissions';
import { logger } from '../../../utils/logger';
import { useHeaderBanner } from '../../hooks/useHeaderBanner';
import type { AuthOrg } from '../../services/auth';
import * as serverOrgs from '../../../api/server/orgs';
import { ENTITY_STATUS } from '../../constants';
import voicebrainIcon from '../../../assets/images/voicebrain-icon.svg';

export const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showOrgDropdown, setShowOrgDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Permissions
  const { isSuperAdmin } = usePermissions();
  const { content: bannerContent } = useHeaderBanner();

  // V2 org state (from OrgProvider)
  const { currentOrg, orgs, setCurrentOrg, refreshOrgs } = useOrg();

  // Hide org picker for non-superadmins with a single org
  const showOrgPicker = isSuperAdmin || orgs.length > 1;

  // New Organization modal state (super-admin only)
  const [isNewOrgModalOpen, setIsNewOrgModalOpen] = useState(false);
  const [isDeactivatedModalOpen, setIsDeactivatedModalOpen] = useState(false);
  const [hasInactiveOrgs, setHasInactiveOrgs] = useState(false);

  // Handle new org creation from dropdown modal
  const handleNewOrgSaved = async (
    org: serverOrgs.Organization | serverOrgs.OrganizationWithUsers
  ) => {
    await refreshOrgs();
    // flushSync forces React to synchronously commit ALL pending state
    // updates — including the setUser() from refreshOrgs still in the
    // batch queue. Without this, orgsMap derives from stale user state
    // and the new org resolves to null → "Access Denied".
    flushSync(() => {
      setCurrentOrg(org.org_id);
    });
    navigate('/v2/settings');
    logger.info('[Header] New organization created, switched to:', org.org_id);
  };

  // Organization name and list from OrgProvider
  const organizationName = currentOrg?.name || 'Select Organization';

  /**
   * Build the new URL path when switching organizations
   * Takes the current page into account and redirects to the org version of that page
   */
  const getOrgAwarePath = (newOrgId: string): string => {
    const currentPath = location.pathname;

    // Pattern: /v2/organizations/:org_id/...
    const orgPathRegex = /^\/v2\/organizations\/[^/]+/;

    if (orgPathRegex.test(currentPath)) {
      // Replace old org_id with new one
      const newPath = currentPath.replace(orgPathRegex, `/v2/organizations/${newOrgId}`);

      // If on channel detail page, go to channels list (channel may not exist in new org)
      if (newPath.includes('/channels/')) {
        return `/v2/organizations/${newOrgId}/channels`;
      }

      return newPath;
    }

    // For pages without org in URL, map to org-aware equivalent
    if (currentPath === '/v2/channels' || currentPath.startsWith('/v2/channels/')) {
      return `/v2/organizations/${newOrgId}/channels`;
    }

    // For other pages (assistant, dashboard, settings), keep the same path
    // These pages will pick up the new org from OrgProvider context
    return currentPath;
  };

  // Check for inactive orgs when dropdown opens (super-admin only)
  useEffect(() => {
    if (!showOrgDropdown || !isSuperAdmin) return;

    serverOrgs
      .listOrgs({ includeInactive: true })
      .then((allOrgs) => {
        setHasInactiveOrgs(allOrgs.some((org) => Number(org.status) === ENTITY_STATUS.INACTIVE));
      })
      .catch(() => {
        // Silently fail — button just stays hidden
      });
  }, [showOrgDropdown, isSuperAdmin]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowOrgDropdown(false);
      }
    };

    if (showOrgDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showOrgDropdown]);

  const handleOrgClick = () => {
    setShowOrgDropdown(!showOrgDropdown);
  };

  const handleSelectOrg = (org: AuthOrg) => {
    setShowOrgDropdown(false);

    // Don't do anything if selecting the same org
    if (org.org_id === currentOrg?.org_id) {
      return;
    }

    // Calculate the new path before the org changes
    const newPath = getOrgAwarePath(org.org_id);

    // Update V2 org state (OrgProvider will trigger setupOrganization)
    setCurrentOrg(org.org_id);

    // Navigate to org-aware page, clearing filter search params
    navigate({ pathname: newPath, search: '' });
  };

  return (
    <header className="h-14 md:h-20 bg-vb-surface border-b border-vb-border flex items-center justify-between px-3 md:px-6 dark:bg-vb-surface-dark dark:border-vb-border-dark">
      {/* Left side - Logo and Org selector */}
      <div className="flex items-center gap-2 md:gap-4 min-w-0">
        <div className="flex items-center gap-2 mr-2 flex-shrink-0">
          <img
            src={voicebrainIcon}
            alt="VoiceBrain"
            className="h-7 md:h-8 text-vb-text-primary dark:invert"
          />
          <span className="hidden md:inline text-lg font-semibold text-vb-text-primary dark:text-vb-text-primary-dark">
            VoiceBrain
          </span>
        </div>

        {/* Organization Selector */}
        {showOrgPicker && (
          <div className="relative min-w-0" ref={dropdownRef}>
            <VBButton
              variant="secondary"
              size="md"
              onClick={handleOrgClick}
              icon={<HiChevronDown className="w-4 h-4" />}
              iconPosition="right"
              className="max-w-[140px] md:max-w-none"
            >
              <span className="truncate">{organizationName}</span>
            </VBButton>

            {/* Dropdown Menu */}
            {showOrgDropdown && orgs.length > 0 && (
              <div className="absolute top-full left-0 mt-2 w-64 bg-vb-surface dark:bg-vb-surface-dark border border-vb-border dark:border-vb-border-dark rounded-lg shadow-lg py-2 z-50">
                <div className="px-3 py-2 text-xs font-semibold text-vb-text-secondary dark:text-vb-text-secondary-dark uppercase">
                  Select Organization
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {orgs.map((org) => (
                    <button
                      key={org.org_id}
                      onClick={() => handleSelectOrg(org)}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-vb-surface-hover dark:hover:bg-vb-surface-hover-dark transition-colors ${
                        org.org_id === currentOrg?.org_id
                          ? 'bg-vb-bg-secondary dark:bg-vb-bg-secondary-dark text-vb-primary font-medium'
                          : 'text-vb-text-primary dark:text-vb-text-primary-dark'
                      }`}
                    >
                      {org.name}
                    </button>
                  ))}
                </div>
                {isSuperAdmin && (
                  <>
                    <div className="border-t border-vb-border dark:border-vb-border-dark my-1" />
                    <button
                      onClick={() => {
                        setShowOrgDropdown(false);
                        setIsNewOrgModalOpen(true);
                      }}
                      className="w-full text-left px-3 py-2 text-sm text-vb-primary dark:text-vb-primary-dark hover:bg-vb-surface-hover dark:hover:bg-vb-surface-hover-dark transition-colors flex items-center gap-2"
                    >
                      <HiPlus className="w-4 h-4" />
                      New Organization
                    </button>
                    {hasInactiveOrgs && (
                      <button
                        onClick={() => {
                          setShowOrgDropdown(false);
                          setIsDeactivatedModalOpen(true);
                        }}
                        className="w-full text-left px-3 py-2 text-sm text-vb-text-secondary dark:text-vb-text-secondary-dark hover:bg-vb-surface-hover dark:hover:bg-vb-surface-hover-dark transition-colors flex items-center gap-2"
                      >
                        <HiArchiveBox className="w-4 h-4" />
                        Deactivated Orgs
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Center - page banner slot */}
      {bannerContent && <div className="flex-1 min-w-0 px-4">{bannerContent}</div>}

      {/* Right side - Actions */}
      <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
        {/* Profile Dropdown - mobile only (desktop has it in sidebar) */}
        <div className="md:hidden">
          <ProfileDropdown position="header" />
        </div>
      </div>

      {/* New Organization Modal (super-admin only) */}
      <OrganizationModal
        isOpen={isNewOrgModalOpen}
        onClose={() => setIsNewOrgModalOpen(false)}
        onSaved={handleNewOrgSaved}
      />

      {/* Deactivated Organizations Modal (super-admin only) */}
      <DeactivatedOrgsModal
        isOpen={isDeactivatedModalOpen}
        onClose={() => setIsDeactivatedModalOpen(false)}
        onReactivated={async (orgId) => {
          await refreshOrgs();
          flushSync(() => {
            setCurrentOrg(orgId);
          });
          setIsDeactivatedModalOpen(false);
          navigate('/v2/settings');
        }}
      />
    </header>
  );
};
